import React, { useEffect, useState } from 'react';
import { Backdrop } from './CheckRequiredAppVersion.styles';
import { getApiVersions } from '../../apis/apiVersionsAPI';
import { ReactComponent as CosafeLogo } from '../../assets/brand/logo-with-text.svg';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { useOnlineStatus } from './useOnlineStatus';
import isElectron from 'is-electron';
import { API_VERSION, SUPPORT_APP_UPDATE_URL } from '../../config/app';

type CheckRequiredAppVersionProps = {
  children: React.ReactNode;
};

export function CheckRequiredAppVersion({ children }: CheckRequiredAppVersionProps) {
  const [hasCheckedApiVersion, setHasCheckedApiVersion] = useState(false);
  const [isUpdateRequired, setIsUpdateRequired] = useState(false);

  const onAppForeground = () => {
    if (isElectron()) {
      setHasCheckedApiVersion(false);
    }
  };

  const onAppOnline = () => {
    if (isElectron()) {
      setHasCheckedApiVersion(false);
    }
  };

  useOnlineStatus(onAppOnline);

  useEffect(() => {
    if (isElectron()) {
      const subscription = window.electronApi?.onAppForeground(() => onAppForeground());
      return () => subscription?.remove();
    }
  }, []);

  useEffect(() => {
    if (hasCheckedApiVersion) {
      return;
    }

    const checkApiVersion = async () => {
      try {
        if (!isElectron()) {
          setIsUpdateRequired(false);
          return;
        }

        const { data, error } = await getApiVersions();
        if (error) {
          throw new Error('Failed to fetch api versions');
        }
        setIsUpdateRequired(API_VERSION < data.minimum);
      } catch (ignoreError) {
        // If there is an error when checking the API version, we assume that the version does not require update and render the app.
        setIsUpdateRequired(false);
      } finally {
        setHasCheckedApiVersion(true);
      }
    };

    checkApiVersion();
  }, [hasCheckedApiVersion]);

  if (isUpdateRequired) {
    return (
      <Backdrop $blocking={false}>
        <CosafeLogo id="cosafeLogo" />
        <ConfirmDialog
          title={'update_app_required_header'}
          description="update_app_required_message"
          blockOutsideClick={true}
          dismiss={() => setIsUpdateRequired(false)}
          onSubmit={() => window.electronApi?.sendOpenLink(`${SUPPORT_APP_UPDATE_URL}?lang=en`)}
          confirmText={'update_app_required_button'}
        />
      </Backdrop>
    );
  }

  return (
    <>
      {!hasCheckedApiVersion && <Backdrop $blocking={true} />}
      {children}
    </>
  );
}
